import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, MessageCircle, ClipboardList, Package, Boxes, Volume2, VolumeX, ClipboardCheck, Lock, ShieldAlert } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const iconFor = (t) => ({ mesaj: MessageCircle, pos_mesaj: MessageCircle, siparis: ClipboardList, stok: Boxes, urun: Package, hazirlik: ClipboardCheck, pos_sepet: Lock, guvenlik: ShieldAlert }[t] || Bell);

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(() => localStorage.getItem("ge_mute") === "1");
  const seen = useRef(new Set());
  const first = useRef(true);
  const audioRef = useRef(null);

  const unread = items.filter((n) => !n.read).length;

  const playBeep = () => {
    if (localStorage.getItem("ge_mute") === "1") return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1245, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      osc.start(); osc.stop(ctx.currentTime + 0.36);
    } catch {}
  };

  const toggleMute = () => {
    const next = !muted; setMuted(next); localStorage.setItem("ge_mute", next ? "1" : "0");
  };

  const poll = async (opts = {}) => {
    try {
      const { data } = await api.get("/notifications");
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      // ephemeral toasts for freshly-arrived unread notifications (silentType: sayfa zaten uyardı → yalnız listeye/rozete düşer)
      let hasNew = false;
      list.filter((n) => !n.read).forEach((n) => {
        if (!seen.current.has(n.id)) {
          seen.current.add(n.id);
          if (!first.current && !n.meta?.silent && !(opts.silentType && n.type === opts.silentType)) {
            hasNew = true;
            const Icon = iconFor(n.type);
            toast.custom((id) => (
              <div className="flex items-start gap-3 bg-[#0b0f19] text-white rounded-xl p-4 shadow-2xl border border-primary/40 w-[340px]">
                <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{n.title}</div>
                  <div className="text-xs text-white/70 line-clamp-2">{n.body}</div>
                </div>
              </div>
            ), { duration: 18000 });
          }
        } else {
          seen.current.add(n.id);
        }
      });
      if (hasNew) playBeep();
      first.current = false;
    } catch {}
  };

  useEffect(() => {
    poll();
    const iv = setInterval(() => poll(), 8000);
    // Sayfalar anlık yoklama isteyebilir (ör. yönetici depo müdürünün sepetine katıldı → zil bildirimi hemen düşsün)
    const onKick = (e) => poll((e && e.detail) || {});
    window.addEventListener("ge:notifications-poll", onKick);
    return () => { clearInterval(iv); window.removeEventListener("ge:notifications-poll", onKick); };
    // eslint-disable-next-line
  }, []);

  const markRead = async () => {
    await api.post("/notifications/read");
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o && unread) markRead(); }}>
      <PopoverTrigger asChild>
        <button className="relative h-10 w-10 rounded-full hover:bg-muted flex items-center justify-center" data-testid="notif-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-count">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notif-panel">
        <div className="p-3 border-b font-head font-bold flex items-center justify-between">
          <span>Bildirimler</span>
          <button onClick={toggleMute} title={muted ? "Sesi aç" : "Sesi kapat"} data-testid="notif-mute"
            className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center">
            {muted ? <VolumeX className="h-4 w-4 text-muted-foreground" /> : <Volume2 className="h-4 w-4 text-primary" />}
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Bildirim yok</div>
          ) : (
            items.slice(0, 30).map((n) => {
              const Icon = iconFor(n.type);
              return (
                <div key={n.id} className={`flex items-start gap-3 p-3 border-b last:border-0 ${!n.read ? "bg-primary/5" : ""}`}>
                  <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-primary" /></div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{n.title}</div>
                    <div className="text-xs text-muted-foreground">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(n.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
